import { reportServerSideExperiment } from '@redux/experiments';
import { useAppDispatch } from '@redux/hooks';
import { Logger } from '@utils/logger/logger';
import { useCallback, useEffect, useRef } from 'react';
import type { Id } from 'types/experiments';

type ObserverReporterConfig = {
	experimentId: Id;
	variant: string | null;
	/**
	 * Additional requirements for reporting the experiment beyond just having
	 * a variant assigned and the relevant element being in the viewport.
	 */
	entranceCriteria?: () => boolean;
	/**
	 * Message to log when the experiment is reported.
	 * Should include the experiment name and variant name for clarity.
	 */
	logMessage: string;
};

/**
 * Custom hook that reports experiment assignment(s) when an element enters the viewport.
 */
export function useObserverExperimentReporter<T extends HTMLElement>(
	configs: ObserverReporterConfig[],
) {
	const dispatch = useAppDispatch();
	const elementRef = useRef<T | null>(null);
	const reportedIds = useRef(new Set<Id>());
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		if (configs.length === 0) return;

		const observer = new IntersectionObserver(
			([{ isIntersecting }]) => {
				if (!isIntersecting) return;

				for (const config of configs) {
					const hasVariant = config.variant !== null;
					const criteriaMet = config.entranceCriteria?.() ?? true;
					const notReported = !reportedIds.current.has(config.experimentId);

					if (hasVariant && criteriaMet && notReported) {
						dispatch(reportServerSideExperiment(config.experimentId));
						Logger.log({ log_message: config.logMessage });
						reportedIds.current.add(config.experimentId);
					}
				}

				const finishedReporting = configs.every(({ experimentId, variant }) => {
					return !variant || reportedIds.current.has(experimentId);
				});

				if (finishedReporting) {
					observer.disconnect();
					if (observerRef.current === observer) {
						observerRef.current = null;
					}
				}
			},
			{ threshold: 0.1 },
		);

		if (elementRef.current) {
			observer.observe(elementRef.current);
		}

		observerRef.current = observer;

		return () => {
			observer.disconnect();
			if (observerRef.current === observer) {
				observerRef.current = null;
			}
		};
	}, [configs, dispatch]);

	const refCallback = useCallback((element: T | null) => {
		if (observerRef.current && elementRef.current) {
			observerRef.current.unobserve(elementRef.current);
		}

		elementRef.current = element;

		if (observerRef.current && element !== null) {
			observerRef.current.observe(element);
		}
	}, []);

	return refCallback;
}
